import { createContext, useContext, useEffect, useState } from "react";
import { Smartphone, X, Share, Bell, BellRing } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PwaCtx = createContext({ enabled: false, installed: false, canPrompt: false, isIos: false, install: async () => {} });
export const usePwa = () => useContext(PwaCtx);

export function PwaProvider({ children }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const installed = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);
  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    if (!user || user.role === "super_admin") { setEnabled(false); return; }
    api.get("/tenant").then((r) => { setEnabled(!!r.data.pwa_enabled); setTenantId(r.data.id); }).catch(() => setEnabled(false));
  }, [user]);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  useEffect(() => {
    const head = document.head;
    const old = head.querySelectorAll("[data-pwa]");
    old.forEach((n) => n.remove());
    if (!enabled || !tenantId) {
      navigator.serviceWorker?.getRegistrations?.().then((rs) => rs.forEach((r) => r.unregister()));
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL;
    const add = (tag, attrs) => { const el = document.createElement(tag); Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v)); el.setAttribute("data-pwa", "1"); head.appendChild(el); };
    add("link", { rel: "manifest", href: `${base}/api/tenant/${tenantId}/pwa/manifest.webmanifest` });
    add("link", { rel: "apple-touch-icon", href: `${base}/api/tenant/${tenantId}/pwa/icon-180.png` });
    add("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
    add("meta", { name: "mobile-web-app-capable", content: "yes" });
    add("meta", { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
    if (installed) {
      const platform = isIos ? "ios" : /android/i.test(navigator.userAgent) ? "android" : "desktop";
      api.post("/pwa/heartbeat", { platform, standalone: true }).catch(() => {});
    }
  }, [enabled, tenantId, installed, isIos]);

  const install = async () => {
    if (!prompt) return false;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setPrompt(null);
    return outcome === "accepted";
  };

  return <PwaCtx.Provider value={{ enabled, installed, canPrompt: !!prompt, isIos, install }}>{children}</PwaCtx.Provider>;
}

export function PushToggleButton({ className = "" }) {
  const { enabled } = usePwa();
  const [state, setState] = useState("idle"); // idle | on | busy | denied | unsupported
  useEffect(() => {
    if (!enabled) return;
    if (!("Notification" in window) || !("PushManager" in window) || !("serviceWorker" in navigator)) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    api.get("/push/status").then((r) => setState(r.data.subscriptions > 0 && Notification.permission === "granted" ? "on" : "idle")).catch(() => {});
  }, [enabled]);
  if (!enabled || state === "unsupported") return null;
  const b64ToU8 = (s) => { const p = "=".repeat((4 - (s.length % 4)) % 4); const r = atob((s + p).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...r].map((c) => c.charCodeAt(0))); };
  const turnOn = async () => {
    setState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState("denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.public_key) });
      await api.post("/push/subscribe", { subscription: sub.toJSON(), platform: navigator.userAgent.slice(0, 60) });
      setState("on");
    } catch (e) { setState("idle"); }
  };
  const turnOff = async () => {
    setState("busy");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); await sub.unsubscribe(); }
    } catch {}
    setState("idle");
  };
  const label = state === "on" ? "Push reminders on" : state === "busy" ? "…" : state === "denied" ? "Push blocked in browser" : "Turn on push reminders";
  return (
    <button onClick={state === "on" ? turnOff : turnOn} disabled={state === "busy" || state === "denied"} data-testid="push-toggle-btn" data-state={state}
      title="Retry-call and awaiting-reply reminders as phone notifications"
      className={`w-full flex items-center justify-center gap-2 border py-1.5 text-xs font-mono uppercase tracking-wider transition-colors disabled:opacity-50 ${state === "on" ? "border-[#3E8F99] text-[#3E8F99] hover:bg-white/10" : "border-white/20 text-white/80 hover:bg-white/10"} ${className}`}>
      {state === "on" ? <BellRing className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Bell className="w-3.5 h-3.5" strokeWidth={1.5} />} {label}
    </button>
  );
}

export function InstallAppButton({ className = "" }) {
  const { enabled, installed, canPrompt, isIos, install } = usePwa();
  const [showIos, setShowIos] = useState(false);
  if (!enabled || installed) return null;
  const click = () => { if (canPrompt) install(); else setShowIos(true); };
  return (
    <>
      <button onClick={click} data-testid="pwa-install-btn"
        className={`w-full flex items-center justify-center gap-2 border border-white/20 text-white/80 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-white/10 transition-colors ${className}`}>
        <Smartphone className="w-3.5 h-3.5" strokeWidth={1.5} /> Install app
      </button>
      {showIos && <IosInstallSheet isIos={isIos} onClose={() => setShowIos(false)} />}
    </>
  );
}

function IosInstallSheet({ isIos, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 p-4" data-testid="pwa-install-help" onClick={onClose}>
      <div className="bg-white text-[#1C2530] w-full max-w-md p-5 border border-[#DFDAD0]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Add to Home Screen</div>
          <button onClick={onClose} data-testid="pwa-install-help-close"><X className="w-4 h-4" /></button>
        </div>
        {isIos ? (
          <ol className="text-sm space-y-2 list-decimal pl-5">
            <li>Tap the <Share className="inline w-4 h-4 -mt-0.5" /> <b>Share</b> button in Safari's toolbar.</li>
            <li>Scroll and tap <b>Add to Home Screen</b>.</li>
            <li>Tap <b>Add</b>. The CRM opens full-screen from your home screen.</li>
          </ol>
        ) : (
          <ol className="text-sm space-y-2 list-decimal pl-5">
            <li>Open the browser menu (⋮).</li>
            <li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
            <li>Confirm. The CRM opens full-screen from your home screen.</li>
          </ol>
        )}
      </div>
    </div>
  );
}

export function InstallBanner() {
  const { enabled, installed, canPrompt, install, isIos } = usePwa();
  const key = "pwa-banner-dismissed";
  const [hidden, setHidden] = useState(() => localStorage.getItem(key) === "1");
  const [showIos, setShowIos] = useState(false);
  if (!enabled || installed || hidden) return null;
  const dismiss = () => { localStorage.setItem(key, "1"); setHidden(true); };
  const click = () => { if (canPrompt) install().then((ok) => ok && dismiss()); else setShowIos(true); };
  return (
    <div className="md:hidden flex items-center gap-3 border border-[#DFDAD0] bg-white p-3" data-testid="pwa-install-banner">
      <Smartphone className="w-5 h-5 text-[#E50914] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1C2530]">Install the CRM on your phone</div>
        <div className="text-xs text-[#1C2530]/55">Opens full-screen from your home screen, like a native app.</div>
      </div>
      <button onClick={click} data-testid="pwa-banner-install" className="bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-medium">Install</button>
      <button onClick={dismiss} data-testid="pwa-banner-dismiss" aria-label="Dismiss" className="text-[#1C2530]/40 hover:text-[#1C2530]"><X className="w-4 h-4" /></button>
      {showIos && <IosInstallSheet isIos={isIos} onClose={() => setShowIos(false)} />}
    </div>
  );
}
